import { TeamPicker } from "@/components/pickers/team-picker";
import { UserAttendanceStatePicker } from "@/components/pickers/user-attendance-state-picker";
import { UserAttendanceTypePicker } from "@/components/pickers/user-attendance-type-picker";
import { client } from "@/hono";
import { useTeamPicker } from "@/hooks/use-team-picker";
import { Button } from "@msuite/ui/button";
import { ContentBox } from "@msuite/ui/content-box";
import { parseAsStringLiteral, useQueryState } from "@msuite/ui/libs/nuqs";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { DataTable, type Row } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { getCanApproveAttendance } from "@msuite/utils/attendances";
import { moment } from "@msuite/utils/date";
import type { OrganizationAttendanceListItem } from "@msuite/utils/types";
import { useCallback, useMemo, useState } from "react";
import { columns } from "./table-config";
import { UserAttendancesDetailsRow } from "./user-attendances-details-row";
import { UserAttendancesMenu } from "./user-attendances-menu";

export function UserAttendances() {
	/** Hooks */
	const teamPickerController = useTeamPicker({ membership: "leader" });
	const { t } = useTranslation();

	/** State */
	const [startDate, setStartDate] = useState(moment().startOf("month").db());
	const [endDate, setEndDate] = useState(moment().endOf("month").db());
	const [attendanceType, setAttendanceType] = useQueryState(
		"attendance-type",
		parseAsStringLiteral(["all", "work", "break"] as const).withDefault("work"),
	);
	const [attendanceState, setAttendanceState] = useQueryState(
		"attendance-state",
		parseAsStringLiteral(["all", "pending", "approved", "rejected"] as const).withDefault(
			"pending",
		),
	);

	/** Functions */
	const handleDatesChange = useCallback((start: string, end: string) => {
		setStartDate(start);
		setEndDate(end);
	}, []);

	function handleSelectGroupRows(row: any) {
		const selectableRows =
			row.subRows?.filter((r: any) => getCanApproveAttendance(r.original)) ?? [];
		const allSelected =
			selectableRows.length > 0 && selectableRows.every((r: any) => r.getIsSelected?.());
		selectableRows.forEach((r: any) => {
			r.toggleSelected(!allSelected);
		});
	}

	/** Query */
	const query = useQuery({
		queryKey: ["attendances", teamPickerController.teamId, startDate, endDate, attendanceState],
		queryFn: async () => {
			if (!teamPickerController.teamId) throw new Error("Team ID needed");

			const response = await client.organizations.attendances.$get({
				query: {
					teamId: teamPickerController.teamId,
					startDate,
					endDate,
					state: attendanceState === "all" ? undefined : attendanceState,
				},
			});
			const data = await responseHandler(response);
			return data.attendances;
		},
		enabled: !!teamPickerController.teamId,
	});

	/** Computed */
	const filtered = useMemo(() => {
		if (attendanceType === "all") return query.data;
		return guardArray(query.data).filter((a) => a.type === attendanceType);
	}, [attendanceType, query.data]);

	/** Render */
	return (
		<ContentBox>
			<DataTable
				data={filtered}
				columns={columns}
				refetch={query.refetch}
				isLoading={query.isLoading}
				queryKey={query.queryKey}
				grouping={["userName", "date"]}
				initialColumnVisibility={{ userName: false }}
				renderGroupHeader={(row) => {
					const selectableRows =
						row.subRows?.filter((r: Row<OrganizationAttendanceListItem>) =>
							getCanApproveAttendance(r.original),
						) ?? [];
					const allSelected =
						selectableRows.length > 0 &&
						selectableRows.every((r: Row<OrganizationAttendanceListItem>) => r.getIsSelected?.());
					const buttonLabel = allSelected ? t("general.deselect_all") : t("general.select_all");

					if (row.groupingColumnId === "date") {
						return (
							<div className="h-stack justify-between">
								<p>{moment(row.original.startTime).dateFormat()}</p>
								<div className="-my-2">
									<Button
										size="xxs"
										variant="ghost"
										onClick={() => handleSelectGroupRows(row)}
									>
										{buttonLabel}
									</Button>
								</div>
							</div>
						);
					} else {
						return (
							<div className="h-stack justify-between">
								<p>{row.original.userName}</p>
								<div className="-my-2">
									<Button
										size="xxs"
										variant="ghost"
										onClick={() => handleSelectGroupRows(row)}
									>
										{buttonLabel}
									</Button>
								</div>
							</div>
						);
					}
				}}
				getRowCanExpand={() => true}
				expandSubRowsByDefault={false}
				enableRowSelection={(row) => {
					return getCanApproveAttendance(row.original);
				}}
				enableMultiRowSelection
				renderSubRows={(row) => {
					return <UserAttendancesDetailsRow row={row} />;
				}}
				menu={<UserAttendancesMenu onDatesChange={handleDatesChange} />}
				persistenceKey="attendances-table"
				slots={{
					"below-search": () => (
						<div className="w-full h-stack justify-between">
							<TeamPicker controller={teamPickerController} />
							<div className="h-stack">
								<UserAttendanceTypePicker
									value={attendanceType}
									onChange={setAttendanceType}
								/>
								<UserAttendanceStatePicker
									value={attendanceState}
									onChange={setAttendanceState}
								/>
							</div>
						</div>
					),
				}}
			/>
		</ContentBox>
	);
}
